/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Heading, HFlow, Icon, Tag, Text, Tooltip, VFlow } from 'bold-ui'
import AccordionList from 'components/accordion/AccordionList'
import { confirm } from 'components/modals/confirm'
import { ButtonLink } from 'components/route'
import { useHistoricoPrescricaoQuery } from 'graphql/hooks.generated'
import {
  AtendimentoProfissional,
  HistoricoPrescricaoQueryInput,
  ReceitaMedicamento,
  TipoReceitaEnum,
  UnidadeMedidaTempoEnum,
} from 'graphql/types.generated'
import { isEmpty } from 'lodash'
import moment from 'moment'
import React, { useCallback, useState } from 'react'
import { useRouteMatch } from 'react-router'
import { useHistory } from 'react-router-dom'
import { tipoReceitaRecord } from 'types/enums'

import { periodoTratamento } from '../components/PeriodoTratamentoSelectField'
import { MedicamentoFormModel } from '../MedicamentoFormModel'
import { convertReceitaMedicamentoToMedicamento } from '../utils/prescricaoConverter'
import HistoricoPrescricaoTableFilter from './HistoricoPrescricaoTableFilter'

export interface HistoricoPrescricaoTableProps {
  prontuarioId: ID
  setMedicamentosToReplicate(medicamentos: MedicamentoFormModel[]): void
}

export default function HistoricoPrescricaoTable(props: HistoricoPrescricaoTableProps) {
  const { prontuarioId, setMedicamentosToReplicate } = props
  const history = useHistory()
  const match = useRouteMatch()
  const [currentFilter, setCurrentFilter] = useState<HistoricoPrescricaoQueryInput>({
    prontuarioId,
    pageParams: {
      size: 5,
    },
  })
  const [isReplicarVersaoAntiga, setIsReplicarVersaoAntiga] = useState<boolean>(true)

  const { data, loading } = useHistoricoPrescricaoQuery({
    variables: { input: currentFilter },
  })
  const historico = data?.historicoPrescricao

  const handleOnClickReplicarMedicamento = useCallback(
    (receitaMedicamento: ReceitaMedicamento, isVersaoAnterior: boolean) => {
      const medicamento: MedicamentoFormModel = convertReceitaMedicamentoToMedicamento(
        receitaMedicamento,
        isVersaoAnterior
      )
      setMedicamentosToReplicate([medicamento])
      history.push(`${match.url}/adicionar`)
    },
    [history, match.url, setMedicamentosToReplicate]
  )

  const handleOnClickReplicar = useCallback(
    (receitaMedicamento: ReceitaMedicamento[], isVersaoAnterior: boolean) => {
      const medicamentos: MedicamentoFormModel[] = receitaMedicamento.map((item) =>
        convertReceitaMedicamentoToMedicamento(item, isVersaoAnterior)
      )
      medicamentos.sort((a, b) => {
        return (a.registroManual ? a.principioAtivoText : a.principioAtivoCombo.principioAtivo.nome).localeCompare(
          b.registroManual ? b.principioAtivoText : b.principioAtivoCombo.principioAtivo.nome
        )
      })
      setMedicamentosToReplicate(medicamentos)
      history.push(`${match.url}/adicionar`)
    },
    [history, match.url, setMedicamentosToReplicate]
  )

  const iniciaReplicar = useCallback(
    (receitaMedicamento: ReceitaMedicamento[], isReplicarPrescricaoInteira: boolean) => {
      setIsReplicarVersaoAntiga(true)
      isReplicarPrescricaoInteira
        ? handleOnClickReplicar(receitaMedicamento, true)
        : handleOnClickReplicarMedicamento(receitaMedicamento[0], true)
    },
    [handleOnClickReplicar, handleOnClickReplicarMedicamento]
  )

  const handleOnCloseModal = useCallback(
    (receitaMedicamento: ReceitaMedicamento[], isReplicarPrescricaoInteira: boolean) => {
      confirm({
        type: 'primary',
        title: `Deseja replicar ${isReplicarPrescricaoInteira ? 'esta prescrição' : 'este medicamento'}?`,
        body:
          'Este medicamento foi prescrito em versão anterior do sistema. Para concluir sua replicação será necessário completar informações. Deseja continuar?',
        onConfirm: () => iniciaReplicar(receitaMedicamento, isReplicarPrescricaoInteira),
        confirmLabel: 'Continuar',
        cancelLabel: 'Cancelar',
        depthLevel: 2,
        manageOverflow: true,
      })()
    },
    [iniciaReplicar]
  )

  const medicamentoInativo = (receita: ReceitaMedicamento) =>
    !receita.medicamento.medicamentoCatmat?.ativo && !receita.registroManual

  const renderItemHeader = (item: AtendimentoProfissional) => {
    const prof = item.lotacao.profissional

    let subtitle: string = `${moment(item.finalizadoEm).format('DD/MM/YYYY')} | ${item.lotacao.profissional.nome}`
    if (prof.conselhoClasse && prof.ufEmissoraConselhoClasse && prof.ufEmissoraConselhoClasse) {
      subtitle = subtitle.concat(
        ` - ${prof.conselhoClasse.sigla} ${prof.ufEmissoraConselhoClasse.sigla} ${prof.numeroConselhoClasse}`
      )
    }

    return (
      <React.Fragment>
        <HFlow
          style={css`
            width: 100%;
          `}
        >
          <VFlow vSpacing={0}>
            <Heading level={5}>
              {item.receitaMedicamento
                .map(
                  (receitaMedicamento) =>
                    `${receitaMedicamento.medicamento.principioAtivo} ${
                      receitaMedicamento.medicamento.concentracao ?? ''
                    }`
                )
                .join(' | ')}
            </Heading>
            <Text>{subtitle}</Text>
          </VFlow>
        </HFlow>
        <HFlow
          alignItems='center'
          style={css`
            left: 1;
          `}
        >
          <Tooltip
            text={
              !item.receitaMedicamento.every((receita) => medicamentoInativo(receita))
                ? 'Replicar prescrição'
                : 'Estes medicamentos não estão mais presentes na base de dados'
            }
          >
            <ButtonLink
              size='small'
              skin='ghost'
              kind='normal'
              disabled={item.receitaMedicamento.every((receita) => medicamentoInativo(receita))}
              to={!isReplicarVersaoAntiga && `${match.url}/adicionar`}
              onClick={() => {
                item.versaoAnterior
                  ? handleOnCloseModal(
                      item.receitaMedicamento.filter((receita) => !medicamentoInativo(receita)),
                      true
                    )
                  : handleOnClickReplicar(item.receitaMedicamento, item.versaoAnterior)
              }}
              data-testid='ReplicarPrescricaoButton'
            >
              <Icon icon='copyOutline' />
            </ButtonLink>
          </Tooltip>
        </HFlow>
      </React.Fragment>
    )
  }

  const renderItemBody = (item: AtendimentoProfissional) => {
    return item.receitaMedicamento?.map((receita) => (
      <HFlow
        key={receita.id}
        style={css`
          padding: 1rem;
          display: flex;
        `}
      >
        <HFlow
          style={css`
            width: 100%;
          `}
        >
          <VFlow
            style={css`
              width: 30rem;
            `}
          >
            <div>
              <HFlow alignItems='center' hSpacing={0.5}>
                <Heading level={5}>{`${receita.medicamento.principioAtivo} ${
                  receita.medicamento.concentracao ?? ''
                }`}</Heading>
                {receita.tratamentoInterrompido && (
                  <Tag
                    type='normal'
                    style={css`
                      margin-left: 0.5rem;
                    `}
                  >
                    Tratamento interrompido
                  </Tag>
                )}
                {receita.usoContinuo && <Tag type='normal'>Uso contínuo</Tag>}
                {receita.tipoReceita !== TipoReceitaEnum.COMUM && (
                  <Tag type='info'>{tipoReceitaRecord[receita.tipoReceita]}</Tag>
                )}
              </HFlow>
              <Text>
                {`${receita.posologia} | ${receita.aplicacaoMedicamento.nome} ${
                  receita.medicamento.formaFarmaceutica?.nome ? `| ${receita.medicamento.formaFarmaceutica?.nome}` : ''
                }`.trim()}
              </Text>
            </div>
            {receita.tratamentoInterrompido && (
              <VFlow vSpacing={0}>
                <Text fontWeight='bold'>Interrupção</Text>
                {receita.dtInterrupcao && <Text>{`${moment(receita.dtInterrupcao).format('DD/MM/YYYY')}`}</Text>}
                {receita.justificativaInterrupcao && <Text>{receita.justificativaInterrupcao}</Text>}
              </VFlow>
            )}
            <div>
              <Heading level={5}>Recomendações</Heading>
              {receita.recomendacoes ? (
                <Text>{receita.recomendacoes}</Text>
              ) : (
                <Text fontStyle='italic'>Não há recomendações para o medicamento</Text>
              )}
            </div>
          </VFlow>
          <VFlow>
            <HFlow alignItems='center'>
              <Heading level={5}>{`Período: ${moment(receita.dtInicioTratamento).format('DD/MM/YYYY')} 
                - ${
                  item?.versaoAnterior
                    ? ` ${periodoTratamento[receita.medidaTempoTratamento]}`
                    : receita.medidaTempoTratamento !== UnidadeMedidaTempoEnum.INDETERMINADO
                    ? moment(receita.dtFimTratamento).format('DD/MM/YYYY')
                    : `${periodoTratamento[receita.medidaTempoTratamento]}`
                }
              `}</Heading>
            </HFlow>
          </VFlow>
        </HFlow>
        <HFlow
          hSpacing={0}
          alignItems='center'
          style={css`
            left: 1;
          `}
        >
          <Tooltip
            text={
              !medicamentoInativo(receita)
                ? 'Replicar medicamento'
                : 'Este medicamento não está mais presente na base de dados'
            }
          >
            <ButtonLink
              size='small'
              skin='ghost'
              kind='normal'
              disabled={medicamentoInativo(receita)}
              to={!isReplicarVersaoAntiga && `${match.url}/adicionar`}
              onClick={() => {
                item.versaoAnterior
                  ? handleOnCloseModal([receita], false)
                  : handleOnClickReplicarMedicamento(receita, item.versaoAnterior)
              }}
              data-testid='ReplicarMedicamentoButton'
            >
              <Icon icon='copyOutline' />
            </ButtonLink>
          </Tooltip>
        </HFlow>
      </HFlow>
    ))
  }

  const noResultPlaceholder =
    !currentFilter?.query && (!historico || isEmpty(historico.content))
      ? 'Nenhuma prescrição realizada'
      : 'Nenhum registro encontrado'

  return (
    <AccordionList<AtendimentoProfissional>
      header={<HistoricoPrescricaoTableFilter initialValues={currentFilter} onChange={setCurrentFilter} />}
      loading={loading}
      data={historico}
      onChange={setCurrentFilter}
      accordionItemHeader={renderItemHeader}
      accordionItemBody={renderItemBody}
      noResultPlaceholder={noResultPlaceholder}
    />
  )
}
